import { useState, useEffect, useMemo } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { 
  Check, 
  ChevronsUpDown, 
  Plus, 
  Wallet,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Account type to normal side mapping
const ACCOUNT_TYPE_DEFAULTS = {
  asset: 'debit',
  expense: 'debit',
  liability: 'credit',
  equity: 'credit',
  income: 'credit'
};

/**
 * SearchableAccountSelect - Searchable dropdown with "Add New" option for GL accounts
 * @param {Array} accounts - List of accounts to select from
 * @param {string} value - Currently selected account_id
 * @param {function} onValueChange - Callback when selection changes
 * @param {string} workspaceId - Current workspace ID
 * @param {function} onAccountCreated - Callback when a new account is created
 * @param {string} accountType - Filter for account type ('expense', 'income', 'asset', etc.)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {string} testId - data-testid attribute
 */
export function SearchableAccountSelect({
  accounts = [],
  value,
  onValueChange,
  workspaceId,
  onAccountCreated,
  accountType = null, // null means show all types
  placeholder = 'Select account...',
  className,
  testId
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    type: accountType || 'expense',
    normal_side: ACCOUNT_TYPE_DEFAULTS[accountType] || 'debit'
  });

  // Update default type when accountType prop changes
  useEffect(() => {
    if (accountType) {
      setNewAccount(prev => ({
        ...prev,
        type: accountType,
        normal_side: ACCOUNT_TYPE_DEFAULTS[accountType]
      }));
    }
  }, [accountType]);

  // Filter accounts based on search and type
  const filteredAccounts = useMemo(() => {
    let filtered = accounts;
    
    // Filter by type if specified
    if (accountType) {
      filtered = filtered.filter(a => a.type === accountType);
    }
    
    // Filter by search
    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(lower) ||
        a.code?.toLowerCase().includes(lower)
      );
    }
    
    return filtered;
  }, [accounts, search, accountType]);

  // Find selected account
  const selectedAccount = useMemo(() => 
    accounts.find(a => a.account_id === value),
    [accounts, value]
  );

  // Generate next account code suggestion
  const suggestNextCode = () => {
    const typeAccounts = accounts.filter(a => a.type === newAccount.type);
    if (typeAccounts.length === 0) {
      // Default starting codes by type
      const defaults = { asset: '1000', liability: '2000', equity: '3000', income: '4000', expense: '5000' };
      return defaults[newAccount.type] || '1000';
    }
    
    // Find highest code and increment
    const codes = typeAccounts.map(a => parseInt(a.code)).filter(c => !isNaN(c));
    const maxCode = Math.max(...codes);
    return String(maxCode + 10);
  };

  // Handle creating new account
  const handleCreateAccount = async () => {
    if (!newAccount.code.trim() || !newAccount.name.trim()) {
      toast.error('Code and name are required');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/accounts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: newAccount.code,
            name: newAccount.name,
            type: newAccount.type,
            normal_side: newAccount.normal_side,
            is_control: false
          })
        }
      );

      if (response.ok) {
        const createdAccount = await response.json();
        toast.success('Account created');
        
        // Close dialog and reset form
        setShowNewDialog(false);
        setNewAccount({
          code: '',
          name: '',
          type: accountType || 'expense',
          normal_side: ACCOUNT_TYPE_DEFAULTS[accountType] || 'debit'
        });
        
        // Notify parent to refresh accounts list
        if (onAccountCreated) {
          onAccountCreated(createdAccount);
        }
        
        // Select the new account
        onValueChange(createdAccount.account_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create account');
      }
    } catch (error) {
      toast.error('Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  // Pre-fill when opening dialog
  const handleOpenNewDialog = () => {
    setNewAccount(prev => ({
      ...prev,
      code: suggestNextCode(),
      name: search
    }));
    setShowNewDialog(true);
    setOpen(false);
  };

  // Update normal side when type changes
  const handleTypeChange = (type) => {
    setNewAccount(prev => ({
      ...prev,
      type,
      normal_side: ACCOUNT_TYPE_DEFAULTS[type]
    }));
  };

  const typeLabel = accountType ? accountType.charAt(0).toUpperCase() + accountType.slice(1) : 'Account';

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal", className)}
            data-testid={testId}
          >
            {selectedAccount ? (
              <span className="truncate">
                {selectedAccount.code} - {selectedAccount.name}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder={`Search ${typeLabel.toLowerCase()}s...`}
              value={search}
              onValueChange={setSearch}
              data-testid={`${testId}-search`}
            />
            <CommandList>
              {/* Add New Button at top */}
              <CommandGroup>
                <CommandItem
                  onSelect={handleOpenNewDialog}
                  className="text-primary cursor-pointer"
                  data-testid={`add-new-account-btn`}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New {typeLabel} Account
                  {search && <span className="ml-1 text-muted-foreground">"{search}"</span>}
                </CommandItem>
              </CommandGroup>
              
              <CommandSeparator />
              
              {/* Account List */}
              <CommandGroup heading={`${typeLabel} Accounts`}>
                {filteredAccounts.length === 0 ? (
                  <CommandEmpty>
                    No accounts found.
                    <Button 
                      variant="link" 
                      className="p-0 h-auto ml-1"
                      onClick={handleOpenNewDialog}
                    >
                      Create one?
                    </Button>
                  </CommandEmpty>
                ) : (
                  filteredAccounts.map((account) => (
                    <CommandItem
                      key={account.account_id}
                      value={account.account_id}
                      onSelect={() => {
                        onValueChange(account.account_id);
                        setOpen(false);
                        setSearch('');
                      }}
                      data-testid={`account-option-${account.account_id}`}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === account.account_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-mono">{account.code} - {account.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">{account.type}</span>
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* New Account Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                New {typeLabel} Account
              </span>
            </DialogTitle>
            <DialogDescription>
              Create a new GL account to use in this transaction.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account-code">Code *</Label>
                <Input
                  id="account-code"
                  value={newAccount.code}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="5100"
                  className="font-mono"
                  data-testid="new-account-code"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="account-name">Name *</Label>
                <Input
                  id="account-name"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Account name"
                  data-testid="new-account-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account-type">Type</Label>
                <Select 
                  value={newAccount.type} 
                  onValueChange={handleTypeChange}
                  disabled={!!accountType} // Disable if type is fixed
                >
                  <SelectTrigger data-testid="new-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-side">Normal Side</Label>
                <Select 
                  value={newAccount.normal_side} 
                  onValueChange={(v) => setNewAccount(prev => ({ ...prev, normal_side: v }))}
                >
                  <SelectTrigger data-testid="new-account-side">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Account codes typically follow: Assets (1000-1999), Liabilities (2000-2999), 
              Equity (3000-3999), Income (4000-4999), Expenses (5000-9999)
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAccount} 
              disabled={creating || !newAccount.code.trim() || !newAccount.name.trim()}
              data-testid="create-account-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Account
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SearchableAccountSelect;
